import { Stack } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo } from 'react';
import type { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { _ } from '/@/locale';
import { MuiSelect } from '/@/mui/select';
import { setPreferences } from '/@/store/session/actions';
import { getSessionCountries, getSessionCountry, getSessionLanguage } from '/@/store/session/selectors';
import type { ProfileV2FormData } from '../..';
import TabContainer from '../../components/tab-container';

export interface LanguageCountryFormData {
    language: string;
    country: string;
}

const LanguageAndCountryEditTab = forwardRef(
    ({ form }: { form: ReturnType<typeof useForm<ProfileV2FormData>> }, ref) => {
        const dispatch = useDispatch();
        const countries = useSelector(getSessionCountries);
        const countryOptions = useMemo(
            () => countries.map((country) => ({ value: String(country.id), label: country.name })),
            [countries]
        );
        const currentCountry = useSelector(getSessionCountry);
        const currentLanguage = useSelector(getSessionLanguage);

        useEffect(() => {
            if (currentLanguage && currentCountry) {
                form.reset({
                    ...form.getValues(),
                    language: currentLanguage,
                    country: String(currentCountry.id || ''),
                });
            }
        }, [currentLanguage, currentCountry, form]);

        const onSubmit = useCallback(
            async (data: LanguageCountryFormData) => {
                await dispatch(
                    setPreferences({
                        language: data.language,
                        country: Number(data.country),
                        interactive: true,
                    })
                );
            },
            [dispatch]
        );

        const languages = useMemo(
            () => [
                { value: 'es', label: _('Español') },
                { value: 'en', label: _('English') },
                { value: 'pt', label: _('Português') },
            ],
            []
        );

        useImperativeHandle(
            ref,
            () => ({
                onSubmit,
            }),
            [onSubmit]
        );

        return (
            <TabContainer title={_('Idioma y país')}>
                <Grid2 container columns={9}>
                    <Grid2 xs={12} md={3}>
                        <Stack spacing={3} py={2}>
                            <MuiSelect
                                {...form.register('language', {
                                    required: _('El idioma es requerido'),
                                })}
                                options={languages}
                                inputLabel={_('Idioma')}
                                value={form.watch('language')}
                                error={!!form.formState.errors.language}
                                formHelperText={form.formState.errors.language?.message}
                                formControlProps={{ fullWidth: true }}
                            />
                            <MuiSelect
                                {...form.register('country', {
                                    required: _('El país es requerido'),
                                })}
                                options={countryOptions}
                                inputLabel={_('País')}
                                value={form.watch('country')}
                                error={!!form.formState.errors.country}
                                formHelperText={form.formState.errors.country?.message}
                                formControlProps={{ fullWidth: true }}
                            />
                        </Stack>
                    </Grid2>
                </Grid2>
            </TabContainer>
        );
    }
);

export default LanguageAndCountryEditTab;
